import client from 'braintree-web/client';
import paypalCheckout from 'braintree-web/paypal-checkout';
import dataCollector from 'braintree-web/data-collector';
import { notifyApmAvailability, getCountryName } from './apm-utils';
import { t } from '../utils/i18n';

function showCheckoutLoadingOverlay() {
  const overlay = document.getElementById('checkout-loading-overlay');
  if (!overlay) return;
  overlay.dataset.visible = 'true';
  overlay.setAttribute('aria-hidden', 'false');
}

function promptForPhone(initialValue = '') {
  return new Promise((resolve) => {
    const modal = document.getElementById('paypal-phone-prompt');
    const input = document.getElementById('paypal-phone-input');
    const submitBtn = document.getElementById('paypal-phone-submit');
    const cancelBtn = document.getElementById('paypal-phone-cancel');
    const errorEl = document.getElementById('paypal-phone-error');

    if (!modal || !input || !submitBtn || !cancelBtn) {
      resolve(null);
      return;
    }

    function close() {
      modal.style.display = 'none';
      modal.hidden = true;
      submitBtn.removeEventListener('click', onSubmit);
      cancelBtn.removeEventListener('click', onCancel);
      modal.removeEventListener('click', onBackdropClick);
      input.removeEventListener('keydown', onKeydown);
    }

    function onSubmit() {
      const value = (input.value || '').trim();
      if (!value) {
        if (errorEl) errorEl.hidden = false;
        input.focus();
        return;
      }
      if (errorEl) errorEl.hidden = true;
      close();
      resolve(value);
    }

    function onCancel() {
      close();
      resolve(null);
    }

    function onBackdropClick(event) {
      if (event.target === modal) onCancel();
    }

    function onKeydown(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        onSubmit();
      } else if (event.key === 'Escape') {
        onCancel();
      }
    }

    input.value = initialValue || '';
    if (errorEl) errorEl.hidden = true;
    modal.hidden = false;
    modal.style.display = 'flex';
    submitBtn.addEventListener('click', onSubmit);
    cancelBtn.addEventListener('click', onCancel);
    modal.addEventListener('click', onBackdropClick);
    input.addEventListener('keydown', onKeydown);

    setTimeout(() => input.focus(), 0);
  });
}

const initPayPal = async function () {
  const paypalConfigElement = document.getElementById('paypal-config');
  if (!paypalConfigElement) {
    notifyApmAvailability('paypal', false);
    return;
  }

  const paypalConfig = JSON.parse(paypalConfigElement.innerHTML);
  const totalAmount = paypalConfig.totalAmount;
  const clientTokenUrl = paypalConfig.clientTokenUrl;

  const paypalButton = document.getElementById('paypal-button');
  const form = document.getElementById('paypal-form');
  const paymentMethodNonceField = document.getElementById(
    'paypal_payment_method_nonce',
  );
  const deviceDataField = document.getElementById('paypal_device_data');
  const billingAddressField = document.getElementById('paypal_billing_address');

  if (!paypalButton || !form) {
    notifyApmAvailability('paypal', false);
    return;
  }

  let clientToken;
  try {
    const response = await fetch(clientTokenUrl);
    const data = await response.json();
    clientToken = data.clientToken;
  } catch (error) {
    console.error('Error fetching client token:', error);
    notifyApmAvailability('paypal', false);
    return;
  }

  client.create(
    {
      authorization: clientToken,
    },
    function (clientErr, clientInstance) {
      if (clientErr) {
        console.error('Error creating Braintree client:', clientErr);
        notifyApmAvailability('paypal', false);
        return;
      }

      let deviceData = '';
      dataCollector.create(
        {
          client: clientInstance,
        },
        function (dataCollectorErr, dataCollectorInstance) {
          if (dataCollectorErr) {
            console.error('Error creating data collector:', dataCollectorErr);
            return;
          }

          deviceData = dataCollectorInstance.deviceData;
        },
      );

      paypalCheckout.create(
        {
          client: clientInstance,
        },
        function (paypalCheckoutErr, paypalCheckoutInstance) {
          if (paypalCheckoutErr) {
            console.error(
              'Error creating PayPal Checkout instance:',
              paypalCheckoutErr,
            );
            notifyApmAvailability('paypal', false);
            return;
          }

          paypalCheckoutInstance
            .loadPayPalSDK({
              currency: 'USD',
              intent: 'authorize',
            })
            .then(function () {
              if (!window.paypal || !window.paypal.Buttons) {
                console.error('PayPal SDK failed to load');
                notifyApmAvailability('paypal', false);
                return;
              }

              form.style.display = 'inline-block';

              const skeletons = document.getElementById(
                'payment-button-skeletons',
              );
              if (skeletons) {
                skeletons.style.display = 'none';
              }

              notifyApmAvailability('paypal', true);

              window.paypal
                .Buttons({
                  fundingSource: window.paypal.FUNDING.PAYPAL,

                  style: {
                    layout: 'horizontal',
                    color: 'gold',
                    shape: 'rect',
                    label: 'paypal',
                    tagline: false,
                    height: 48,
                  },

                  createOrder: function () {
                    const termsCheckbox = document.getElementById(
                      'cart-terms-checkbox',
                    );
                    if (termsCheckbox && !termsCheckbox.checked) {
                      alert(t('js.billing.accept_terms'));
                      return Promise.reject(new Error('Terms not accepted'));
                    }

                    return paypalCheckoutInstance.createPayment({
                      flow: 'checkout',
                      amount: totalAmount,
                      currency: 'USD',
                      intent: 'authorize',
                      enableShippingAddress: true,
                      shippingAddressEditable: true,
                    });
                  },

                  onApprove: function (data) {
                    return paypalCheckoutInstance
                      .tokenizePayment(data)
                      .then(function (payload) {
                        const details = payload.details || {};
                        const shippingAddress = details.shippingAddress || {};
                        const billingAddress = details.billingAddress || {};

                        const pick = (field) =>
                          billingAddress[field] || shippingAddress[field] || '';

                        const countryCode = pick('countryCode') || 'US';
                        const countryName = getCountryName(countryCode, 'US');

                        const addressLines = [
                          pick('line1'),
                          pick('line2'),
                        ].filter((line) => line.length > 0);

                        const billingAddressData = {
                          givenName: details.firstName || '',
                          familyName: details.lastName || '',
                          phoneNumber: details.phone || '',
                          emailAddress: details.email || '',
                          addressLines: addressLines,
                          locality: pick('city'),
                          administrativeArea: pick('state'),
                          postalCode: pick('postalCode'),
                          countryCode: countryCode,
                          countryName: countryName,
                        };

                        function submitForm() {
                          paymentMethodNonceField.value = payload.nonce;
                          deviceDataField.value = deviceData;
                          billingAddressField.value =
                            JSON.stringify(billingAddressData);
                          showCheckoutLoadingOverlay();
                          form.submit();
                        }

                        setTimeout(() => {
                          promptForPhone(billingAddressData.phoneNumber).then(
                            (phoneNumber) => {
                              if (phoneNumber === null) return;
                              billingAddressData.phoneNumber = phoneNumber;
                              submitForm();
                            },
                          );
                        }, 0);
                      });
                  },

                  onCancel: function () {
                    console.log('User canceled PayPal payment');
                  },

                  onError: function (err) {
                    console.error('PayPal Checkout error:', err);
                  },
                })
                .render('#paypal-button')
                .catch(function (err) {
                  console.error('Error rendering PayPal button:', err);
                  notifyApmAvailability('paypal', false);
                });
            })
            .catch(function (err) {
              console.error('Error loading PayPal SDK:', err);
              notifyApmAvailability('paypal', false);
            });
        },
      );
    },
  );
};

document.addEventListener('readystatechange', function () {
  if (document.readyState === 'complete') {
    initPayPal();
  }
});

export default initPayPal;
